<script setup>
import { computed } from 'vue'

import { CHECKIN_MODAL_ENTRY_POINTS, useCheckinModal } from '@/utils/check-ins/check-ins'
import { BUTTON_SIZES } from '@/utils/components-configurations/app-button'
import { currentUserCanReadObjective } from '@/utils/objectives'
import { includesValue } from '@/utils/prop-validators'

import { CHECKIN_MODAL_TRIGGER_BUTTON_TEST_ID } from '@/components/check-ins/jest-helpers'
import AppButton from '@/components/ui/AppButton/AppButton'

defineOptions({
  name: 'CheckinModalTrigger',
  inheritAttrs: false
})

const props = defineProps({
  entryPoint: {
    type: String,
    validator: v => includesValue(v, CHECKIN_MODAL_ENTRY_POINTS),
    required: true
  },

  okrElement: {
    type: Object,
    required: true
  },

  previewOnly: {
    type: Boolean
  }
})

const { canCreateCheckinToElements, isLaunchAsAnyPreviewMode } = useCheckinModal({
  okrElement: () => props.okrElement,
  entryPoint: () => props.entryPoint
})

const hasReadAccessToElementCheckins = computed(() => {
  return currentUserCanReadObjective(props.okrElement)
})

const isNoAccessToCheckins = computed(() => {
  return props.entryPoint === CHECKIN_MODAL_ENTRY_POINTS.NO_ACCESS
})

const emit = defineEmits({
  'open-checkin-modal': null
})

const canOpenCheckinModal = computed(() => {
  if (isNoAccessToCheckins.value) return false

  // reader can open modal only if there is at least one checkin
  const isReaderCanOpenModal =
    hasReadAccessToElementCheckins.value && isLaunchAsAnyPreviewMode.value

  if (props.previewOnly) return isReaderCanOpenModal

  return isReaderCanOpenModal || canCreateCheckinToElements.value
})

const onTriggerClick = () => {
  if (canOpenCheckinModal.value) {
    emit('open-checkin-modal', {
      okrElement: props.okrElement,
      entryPoint: props.entryPoint
    })
  }
}
</script>

<template>
  <slot
    :can-open-checkin-modal="canOpenCheckinModal"
    :on-trigger-click="onTriggerClick"
    :trigger-styles="{
      cursor: canOpenCheckinModal ? 'pointer' : 'default'
    }"
  >
    <AppButton
      :data-auto-testid="CHECKIN_MODAL_TRIGGER_BUTTON_TEST_ID"
      :data-testid="CHECKIN_MODAL_TRIGGER_BUTTON_TEST_ID"
      :disable="!canOpenCheckinModal"
      :size="BUTTON_SIZES.LG_NEXT"
      class="cmt-Button"
      v-bind="$attrs"
      @click="onTriggerClick"
    >
      <span class="oboard-truncated-text">
        <slot name="trigger-content">
          {{ $t('checkin') }}
        </slot>
      </span>
    </AppButton>
  </slot>
</template>

<style lang="scss" scoped>
.cmt-Button {
  max-width: 100%;
  overflow: hidden;
}
</style>
